/**
 * Раздел «Видеонаблюдение» (поток 8, раздел 6.31 ТЗ): камеры площадок и их состояние, события видеоаналитики с
 * решением человека, журнал просмотров, положение о видеонаблюдении и согласия на биометрию. Видеосервер — у
 * заказчика; здесь только карточки, события, кадры и журнал.
 */
import {
  CAMERA_PURPOSES,
  CAMERA_SOURCES,
  CAMERA_STATES,
  type CameraPurpose,
  type CameraSource,
  type CameraView,
  EVENT_DECISIONS,
  type EventDecision,
  VIDEO_EVENT_KINDS,
  type VideoEventKind,
  type VideoEventView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useCompanyWide } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader } from '../../ui/layout';
import { ProjectFilter, Tabs, useProjectsWith } from '../psd/shared';

type Tab = 'cameras' | 'events' | 'log' | 'legal';
type Search = { tab?: Tab; project?: string; camera?: string; event?: string; decision?: string };

const STATE_TONE: Record<string, Tone> = { online: 'success', no_signal: 'danger', not_connected: 'neutral' };
const kindTitle = (k: string) => VIDEO_EVENT_KINDS[k as VideoEventKind] ?? k;

export function VideoPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const tab: Tab = search.tab ?? 'cameras';
  const setSearch = (patch: Partial<Search>) =>
    navigate({ to: '/video', search: { ...search, ...patch } as never, replace: patch.tab === undefined });
  const auditor = useCompanyWide('video.audit');
  const managers = useProjectsWith('video.manage');
  const [creating, setCreating] = useState(false);
  useCrumbs(
    tab === 'cameras'
      ? null
      : [{ label: tab === 'events' ? 'События' : tab === 'log' ? 'Журнал просмотров' : 'Законность' }],
  );
  return (
    <Page>
      <PageHeader
        title="Видеонаблюдение"
        subtitle="Камеры площадок, события видеоаналитики и журнал просмотров. Видеосервер стоит у вас на площадке: в портал приходят только состояние камер, события и кадры."
        actions={
          tab === 'cameras' && managers.list.length > 0 ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Подключить камеру
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ProjectFilter
          value={search.project}
          onChange={(project) => setSearch({ project })}
          perms={['video.view']}
        />
      </div>
      <Tabs<Tab>
        value={tab}
        onChange={(t) => setSearch({ tab: t })}
        items={[
          { value: 'cameras', label: 'Камеры' },
          { value: 'events', label: 'События' },
          ...(auditor ? [{ value: 'log' as const, label: 'Журнал просмотров' }] : []),
          { value: 'legal', label: 'Законность' },
        ]}
      />
      {tab === 'cameras' ? (
        <CamerasTab projectId={search.project} open={search.camera} setSearch={setSearch} />
      ) : tab === 'events' ? (
        <EventsTab
          projectId={search.project}
          decision={search.decision}
          open={search.event}
          setSearch={setSearch}
        />
      ) : tab === 'log' ? (
        <LogTab projectId={search.project} />
      ) : (
        <LegalTab />
      )}
      {creating ? <CameraForm projectId={search.project} onClose={() => setCreating(false)} /> : null}
    </Page>
  );
}

/* ───────────────────────── Камеры ───────────────────────── */

function CamerasTab({
  projectId,
  open,
  setSearch,
}: {
  projectId?: string;
  open?: string;
  setSearch: (p: Partial<Search>) => void;
}) {
  const q = useQuery({
    queryKey: ['video', 'cameras', projectId ?? null],
    queryFn: () => api<CameraView[]>('/video/cameras', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Камер пока нет">
        Камеру подключает тот, кто меняет проект: проект, объект, место на плане и назначение. Состояние «в
        сети» обновляется по сигналу видеосервера площадки.
      </EmptyState>
    );
  return (
    <>
      <ul className="grid gap-3 md:grid-cols-2">
        {q.data.map((c) => (
          <li key={c.id} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-[15px] font-semibold text-ink">{c.name}</h3>
                <p className="text-[13px] text-ink-2">
                  {c.projectCode}
                  {c.objectName ? ` · ${c.objectName}` : ''} · {CAMERA_PURPOSES[c.purpose as CameraPurpose]}
                </p>
              </div>
              <Badge tone={STATE_TONE[c.state]}>{CAMERA_STATES[c.state]}</Badge>
            </div>
            {c.planPlace ? <p className="mt-2 text-[13px] text-ink-2">Место: {c.planPlace}</p> : null}
            <p className="mt-1 text-[12px] text-ink-2">
              {c.lastSeenAt ? `Сигнал: ${dateTime(c.lastSeenAt)}` : 'Сигнала ещё не было'}
              {c.pendingEvents ? ` · ждут решения: ${c.pendingEvents}` : ''}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setSearch({ camera: c.id })}>
                Карточка
              </Button>
              {c.pendingEvents ? (
                <Button
                  size="sm"
                  onClick={() => setSearch({ tab: 'events', project: c.projectId, decision: 'pending' })}
                >
                  События
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {open ? <CameraDialog id={open} onClose={() => setSearch({ camera: undefined })} /> : null}
    </>
  );
}

interface CameraCard {
  camera: CameraView;
  events: VideoEventView[];
  canManage: boolean;
  canDecide: boolean;
}

function CameraDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['video', 'camera', id],
    queryFn: () => api<CameraCard>(`/video/cameras/${id}`),
  });
  const admin = useCompanyWide('video.biometrics');
  const [editing, setEditing] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['video'] });
  const view = useMutation({
    mutationFn: (body: { kind: 'live' | 'archive'; from?: string; to?: string }) =>
      api<{ message: string }>(`/video/cameras/${id}/view`, { method: 'POST', body }),
    onSuccess: (r) => setNote(r.message),
  });
  const act = useMutation({
    mutationFn: (what: { path: string; body?: unknown; method?: 'POST' | 'PATCH' }) =>
      api(`/video/cameras/${id}${what.path}`, { method: what.method ?? 'POST', body: what.body ?? {} }),
    onSuccess: () => {
      toast('Сохранено');
      refresh();
    },
  });
  const c = q.data?.camera;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={c?.name ?? 'Камера'} width="lg">
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : c ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATE_TONE[c.state]}>{CAMERA_STATES[c.state]}</Badge>
            {c.archived ? <Badge tone="neutral">В архиве</Badge> : null}
            {c.biometricsEnabled ? <Badge tone="warning">Распознавание лиц включено</Badge> : null}
            {c.signPosted ? (
              <Badge tone="success">Табличка размещена</Badge>
            ) : (
              <Badge tone="warning">Нет таблички</Badge>
            )}
          </div>
          <dl className="grid gap-x-6 gap-y-2 text-[14px] md:grid-cols-2">
            <Row k="Проект">
              <Link
                to="/projects/$projectId"
                params={{ projectId: c.projectId }}
                className="inline-flex min-h-11 items-center underline"
              >
                {c.projectCode}
              </Link>
            </Row>
            <Row k="Объект">{c.objectName ?? '—'}</Row>
            <Row k="Назначение">{CAMERA_PURPOSES[c.purpose as CameraPurpose]}</Row>
            <Row k="Направление">{c.direction ?? '—'}</Row>
            <Row k="Место на плане">
              {c.planPlace ?? '—'}
              {c.planX != null && c.planY != null
                ? ` (${Math.round(c.planX * 100)} % × ${Math.round(c.planY * 100)} %)`
                : ''}
            </Row>
            <Row k="Источник">
              {CAMERA_SOURCES[c.sourceKind as CameraSource]}
              {c.externalRef ? `, канал ${c.externalRef}` : ''}
            </Row>
            <Row k="Ответственный">{c.responsibleName ?? '—'}</Row>
            <Row k="Последний сигнал">{c.lastSeenAt ? dateTime(c.lastSeenAt) : 'ещё не было'}</Row>
          </dl>
          <section className="rounded-md border border-line p-3">
            <h4 className="mb-2 text-[14px] font-semibold">Просмотр</h4>
            <p className="mb-3 text-[12px] text-ink-2">
              Каждый просмотр записывается в журнал: кто, какую камеру и какой фрагмент.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <Button
                disabled={c.archived}
                loading={view.isPending}
                onClick={() => view.mutate({ kind: 'live' })}
              >
                Живой просмотр
              </Button>
              <Field label="Архив с">
                <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field label="по">
                <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
              <Button
                disabled={!from || !to}
                loading={view.isPending}
                onClick={() =>
                  view.mutate({
                    kind: 'archive',
                    from: new Date(from).toISOString(),
                    to: new Date(to).toISOString(),
                  })
                }
              >
                Открыть архив
              </Button>
            </div>
            {note ? <p className="mt-3 rounded-md bg-tile px-3 py-2 text-[13px]">{note}</p> : null}
            <ErrorBox error={view.error} className="mt-3" />
          </section>
          <section>
            <h4 className="mb-2 text-[14px] font-semibold">Последние события</h4>
            {q.data!.events.length ? (
              <ul className="divide-y divide-line rounded-md border border-line">
                {q.data!.events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-2 px-3 py-2 text-[13px]">
                    <span>
                      {kindTitle(e.kind)} · {dateTime(e.occurredAt)}
                    </span>
                    <Badge tone={e.decision ? 'neutral' : e.urgent ? 'danger' : 'warning'}>
                      {e.decision ? EVENT_DECISIONS[e.decision as EventDecision] : 'Ждёт решения'}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-2">Событий пока не было.</p>
            )}
          </section>
          <div className="flex flex-wrap gap-2">
            {q.data!.canManage ? (
              <>
                <Button onClick={() => setEditing(true)}>Изменить</Button>
                {c.archived ? (
                  <Button loading={act.isPending} onClick={() => act.mutate({ path: '/restore' })}>
                    Вернуть в работу
                  </Button>
                ) : (
                  <Button loading={act.isPending} onClick={() => act.mutate({ path: '/archive' })}>
                    В архив
                  </Button>
                )}
              </>
            ) : null}
            {admin && !c.archived ? (
              <Button
                loading={act.isPending}
                onClick={() =>
                  act.mutate({
                    path: '',
                    method: 'PATCH',
                    body: { biometricsEnabled: !c.biometricsEnabled },
                  })
                }
              >
                {c.biometricsEnabled ? 'Выключить распознавание лиц' : 'Включить распознавание лиц'}
              </Button>
            ) : null}
          </div>
          <ErrorBox error={act.error} />
          {editing ? <CameraForm camera={c} onClose={() => setEditing(false)} /> : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[12px] text-ink-2">{k}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

function CameraForm({
  camera,
  projectId,
  onClose,
}: {
  camera?: CameraView;
  projectId?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const projects = useProjectsWith('video.manage');
  const [f, setF] = useState({
    projectId: camera?.projectId ?? projectId ?? '',
    objectId: camera?.objectId ?? '',
    name: camera?.name ?? '',
    purpose: (camera?.purpose ?? 'work_zone') as CameraPurpose,
    direction: camera?.direction ?? '',
    planPlace: camera?.planPlace ?? '',
    sourceKind: (camera?.sourceKind ?? 'nvr') as CameraSource,
    externalRef: camera?.externalRef ?? '',
    responsibleId: camera?.responsibleId ?? '',
    signPosted: camera?.signPosted ?? false,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const project = useQuery({
    queryKey: ['project-objects', f.projectId],
    queryFn: () => api<{ objects: Array<{ id: string; name: string }> }>(`/projects/${f.projectId}`),
    enabled: Boolean(f.projectId),
  });
  const people = useQuery({
    queryKey: ['supervision-people', f.projectId, 'video'],
    queryFn: () =>
      api<Array<{ id: string; fullName: string; external: boolean }>>('/supervision/people', {
        query: { projectId: f.projectId },
      }),
    enabled: Boolean(f.projectId),
  });
  const m = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        purpose: f.purpose,
        direction: f.direction || null,
        planPlace: f.planPlace || null,
        sourceKind: f.sourceKind,
        externalRef: f.externalRef || null,
        objectId: f.objectId || null,
        responsibleId: f.responsibleId || null,
        signPosted: f.signPosted,
      };
      return camera
        ? api(`/video/cameras/${camera.id}`, { method: 'PATCH', body })
        : api('/video/cameras', { method: 'POST', body: { ...body, projectId: f.projectId } });
    },
    onSuccess: () => {
      toast(camera ? 'Камера сохранена' : 'Камера подключена');
      qc.invalidateQueries({ queryKey: ['video'] });
      onClose();
    },
  });
  const errors =
    m.error && 'fieldErrors' in m.error
      ? (m.error as { fieldErrors(): Record<string, string> }).fieldErrors()
      : {};
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={camera ? 'Изменить камеру' : 'Подключить камеру'}
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!f.projectId || f.name.length < 2}
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Проект" error={errors.projectId}>
          <Select
            value={f.projectId}
            disabled={Boolean(camera)}
            onChange={(e) => {
              set('projectId', e.target.value);
              set('objectId', '');
              set('responsibleId', '');
            }}
          >
            <option value="">Выберите проект</option>
            {projects.list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Объект">
          <Select value={f.objectId} onChange={(e) => set('objectId', e.target.value)}>
            <option value="">Без объекта</option>
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Название" error={errors.name}>
          <Input value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Назначение">
          <Select value={f.purpose} onChange={(e) => set('purpose', e.target.value as CameraPurpose)}>
            {Object.entries(CAMERA_PURPOSES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Место на плане" hint="Например: угол котлована, ось 4">
          <Input value={f.planPlace} onChange={(e) => set('planPlace', e.target.value)} />
        </Field>
        <Field label="Куда смотрит">
          <Input value={f.direction} onChange={(e) => set('direction', e.target.value)} />
        </Field>
        <Field label="Источник">
          <Select value={f.sourceKind} onChange={(e) => set('sourceKind', e.target.value as CameraSource)}>
            {Object.entries(CAMERA_SOURCES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Канал на видеосервере"
          hint="Имя или номер канала. Адрес потока с паролем в портал не вносится"
          error={errors.externalRef}
        >
          <Input value={f.externalRef} onChange={(e) => set('externalRef', e.target.value)} />
        </Field>
        <Field label="Ответственный" hint="Ему придёт дело «Нет сигнала»">
          <Select value={f.responsibleId} onChange={(e) => set('responsibleId', e.target.value)}>
            <option value="">Не назначен</option>
            {(people.data ?? [])
              .filter((p) => !p.external)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                </option>
              ))}
          </Select>
        </Field>
        <Checkbox
          checked={f.signPosted}
          onChange={(ev) => set('signPosted', ev.target.checked)}
          label="Табличка о видеонаблюдении размещена"
        />
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── События ───────────────────────── */

function EventsTab({
  projectId,
  decision,
  open,
  setSearch,
}: {
  projectId?: string;
  decision?: string;
  open?: string;
  setSearch: (p: Partial<Search>) => void;
}) {
  const filter = decision ?? 'pending';
  const q = useQuery({
    queryKey: ['video', 'events', projectId ?? null, filter],
    queryFn: () =>
      api<VideoEventView[]>('/video/events', {
        query: { projectId, decision: filter === 'all' ? undefined : filter },
      }),
  });
  return (
    <>
      <div className="mb-4 max-w-xs">
        <Select
          aria-label="Решение"
          value={filter}
          onChange={(e) => setSearch({ decision: e.target.value })}
          className="h-11 md:h-10"
        >
          <option value="pending">Ждут решения</option>
          <option value="confirmed">Подтверждённые</option>
          <option value="false_positive">Ложные срабатывания</option>
          <option value="ignored">Не требуют действий</option>
          <option value="all">Все</option>
        </Select>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <EmptyState title="Событий нет">
          События приходят от видеосервера площадки: каска, жилет, опасная зона, номера на въезде, прогресс.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => setSearch({ event: e.id })}
                className="flex min-h-11 w-full items-start justify-between gap-3 rounded-lg border border-line bg-surface p-3 text-left hover:bg-tile"
              >
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-ink">{kindTitle(e.kind)}</span>
                  <span className="block text-[13px] text-ink-2">
                    {e.cameraName} · {e.projectCode} · {dateTime(e.occurredAt)}
                    {e.confidence != null ? ` · ${Math.round(e.confidence * 100)} %` : ''}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  {e.urgent && !e.decision ? <Badge tone="danger">Срочно</Badge> : null}
                  <Badge tone={e.decision ? 'neutral' : 'warning'}>
                    {e.decision ? EVENT_DECISIONS[e.decision as EventDecision] : 'Ждёт решения'}
                  </Badge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open ? <EventDialog id={open} onClose={() => setSearch({ event: undefined })} /> : null}
    </>
  );
}

function EventDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['video', 'event', id],
    queryFn: () =>
      api<{ event: VideoEventView; canDecide: boolean; canRemark: boolean }>(`/video/events/${id}`),
  });
  const [note, setNote] = useState('');
  const [frame, setFrame] = useState<'hidden' | 'shown' | 'failed'>('hidden');
  const [remarkOpen, setRemarkOpen] = useState(false);
  const decide = useMutation({
    mutationFn: (d: EventDecision) =>
      api(`/video/events/${id}/decision`, { method: 'POST', body: { decision: d, note: note || null } }),
    onSuccess: () => {
      toast('Решение записано');
      qc.invalidateQueries({ queryKey: ['video'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
  const e = q.data?.event;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={e ? kindTitle(e.kind) : 'Событие'} width="lg">
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : e ? (
        <div className="flex flex-col gap-4">
          <dl className="grid gap-x-6 gap-y-2 text-[14px] md:grid-cols-2">
            <Row k="Камера">{e.cameraName}</Row>
            <Row k="Проект">
              <Link
                to="/projects/$projectId"
                params={{ projectId: e.projectId }}
                className="inline-flex min-h-11 items-center underline"
              >
                {e.projectCode}
              </Link>
            </Row>
            <Row k="Время">{dateTime(e.occurredAt)}</Row>
            <Row k="Уверенность модели">
              {e.confidence != null ? `${Math.round(e.confidence * 100)} %` : '—'}
            </Row>
            {e.plate ? <Row k="Номер">{e.plate}</Row> : null}
            {e.personCount != null ? <Row k="Людей в кадре">{e.personCount}</Row> : null}
            {e.personName ? <Row k="Опознан (по согласию)">{e.personName}</Row> : null}
            {e.identityDropped ? <Row k="Личность">не установлена: нет согласия на биометрию</Row> : null}
          </dl>
          {e.hasFrame ? (
            <section>
              {frame === 'hidden' ? (
                <Button onClick={() => setFrame('shown')}>Показать кадр</Button>
              ) : frame === 'shown' ? (
                <img
                  src={`/api/v1/video/events/${e.id}/frame`}
                  alt="Кадр события"
                  className="max-h-96 w-full rounded-md border border-line object-contain"
                  onError={() => setFrame('failed')}
                />
              ) : (
                <p className="text-[13px] text-danger">
                  Кадр недоступен: хранилище не подключено или файл удалён.
                </p>
              )}
              <p className="mt-1 text-[12px] text-ink-2">Открытие кадра записывается в журнал просмотров.</p>
            </section>
          ) : null}
          {e.decision ? (
            <section className="rounded-md bg-tile p-3 text-[14px]">
              <p className="font-semibold">{EVENT_DECISIONS[e.decision as EventDecision]}</p>
              <p className="text-ink-2">
                {e.decidedByName}, {e.decidedAt ? dateTime(e.decidedAt) : ''}
              </p>
              {e.decisionNote ? <p className="mt-1">{e.decisionNote}</p> : null}
            </section>
          ) : q.data!.canDecide ? (
            <section className="flex flex-col gap-3">
              <Field label="Комментарий к решению">
                <Textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)} />
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={decide.isPending}
                  onClick={() => decide.mutate('confirmed')}
                >
                  Подтвердить
                </Button>
                <Button loading={decide.isPending} onClick={() => decide.mutate('false_positive')}>
                  Ложное срабатывание
                </Button>
                <Button loading={decide.isPending} onClick={() => decide.mutate('ignored')}>
                  Не требует действий
                </Button>
              </div>
            </section>
          ) : (
            <p className="text-[13px] text-ink-2">Решение принимает проверяющий ОТ и ПБ проекта.</p>
          )}
          <ErrorBox error={decide.error} />
          {e.remarkId ? (
            <p className="text-[14px]">
              Замечание в реестре:{' '}
              <Link
                to="/supervision/remarks/$remarkId"
                params={{ remarkId: e.remarkId }}
                className="inline-flex min-h-11 items-center font-medium underline"
              >
                {e.remarkNumber}
              </Link>
            </p>
          ) : q.data!.canRemark && e.decision !== 'false_positive' && e.decision !== 'ignored' ? (
            <Button onClick={() => setRemarkOpen(true)}>Создать замечание ОТ и ПБ</Button>
          ) : null}
          {remarkOpen ? <RemarkForm event={e} onClose={() => setRemarkOpen(false)} /> : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function RemarkForm({ event, onClose }: { event: VideoEventView; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState('remark');
  const [assigneeId, setAssigneeId] = useState('');
  const people = useQuery({
    queryKey: ['supervision-people', event.projectId, 'respond'],
    queryFn: () =>
      api<Array<{ id: string; fullName: string; external: boolean }>>('/supervision/people', {
        query: { projectId: event.projectId },
      }),
  });
  const m = useMutation({
    mutationFn: () =>
      api<{ number: string }>(`/video/events/${event.id}/remark`, {
        method: 'POST',
        body: { kind, assigneeId: assigneeId || null },
      }),
    onSuccess: (r) => {
      toast(`Создано: ${r.number}`);
      qc.invalidateQueries({ queryKey: ['video'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Замечание по событию"
      description="Запись попадёт в единый реестр замечаний и предписаний; кадр станет фото «до»."
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>
            Создать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Вид">
          <Select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="remark">Замечание</option>
            <option value="prescription">Предписание</option>
            <option value="instruction">Указание</option>
          </Select>
        </Field>
        <Field label="Исполнитель" hint="Получит поручение в «Ждёт меня»">
          <Select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">Не назначен</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Журнал просмотров ───────────────────────── */

function LogTab({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['video', 'log', projectId ?? null],
    queryFn: () =>
      api<
        Array<{
          id: string;
          at: string;
          kind: string;
          userName: string;
          cameraName: string;
          projectCode: string;
          fromAt: string | null;
          toAt: string | null;
        }>
      >('/video/view-log', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data.length) return <EmptyState title="Просмотров ещё не было" />;
  const KIND: Record<string, string> = {
    live: 'Живой просмотр',
    archive: 'Архив',
    event_frame: 'Кадр события',
  };
  return (
    <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
      {q.data.map((l) => (
        <li key={l.id} className="px-4 py-3 text-[14px]">
          <p className="font-medium text-ink">
            {l.userName} · {KIND[l.kind] ?? l.kind}
          </p>
          <p className="text-[13px] text-ink-2">
            {l.cameraName} · {l.projectCode} · {dateTime(l.at)}
            {l.fromAt && l.toAt ? ` · фрагмент ${dateTime(l.fromAt)} — ${dateTime(l.toAt)}` : ''}
          </p>
        </li>
      ))}
    </ul>
  );
}

/* ───────────────────────── Законность ───────────────────────── */

interface Settings {
  archiveDays: number;
  eventDays: number;
  offlineAfterMinutes: number;
  policyApprovedOn: string | null;
  policyNote: string | null;
  canEdit: boolean;
}

function LegalTab() {
  const qc = useQueryClient();
  const admin = useCompanyWide('video.biometrics');
  const q = useQuery({ queryKey: ['video', 'settings'], queryFn: () => api<Settings>('/video/settings') });
  const [f, setF] = useState<Partial<Settings> | null>(null);
  const s = q.data;
  const cur = { ...s, ...f } as Settings;
  const save = useMutation({
    mutationFn: () =>
      api('/video/settings', {
        method: 'PUT',
        body: {
          archiveDays: Number(cur.archiveDays),
          eventDays: Number(cur.eventDays),
          offlineAfterMinutes: Number(cur.offlineAfterMinutes),
          policyApprovedOn: cur.policyApprovedOn || null,
          policyNote: cur.policyNote || null,
        },
      }),
    onSuccess: () => {
      toast('Настройки сохранены');
      setF(null);
      qc.invalidateQueries({ queryKey: ['video'] });
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const edit = Boolean(s?.canEdit);
  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-lg border border-line bg-surface p-4">
        <h3 className="mb-1 text-[16px] font-semibold">Положение о видеонаблюдении и сроки хранения</h3>
        <p className="mb-4 text-[13px] text-ink-2">
          Без утверждённого положения распознавание лиц включить нельзя. Обработка видео и биометрии — на
          серверах в Казахстане, по Закону РК «О персональных данных и их защите».
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Дата утверждения положения">
            <Input
              type="date"
              disabled={!edit}
              value={cur.policyApprovedOn ?? ''}
              onChange={(e) => setF({ ...f, policyApprovedOn: e.target.value || null })}
            />
          </Field>
          <Field label="Реквизиты приказа">
            <Input
              disabled={!edit}
              value={cur.policyNote ?? ''}
              onChange={(e) => setF({ ...f, policyNote: e.target.value })}
            />
          </Field>
          <Field label="Хранение архива, дней">
            <Input
              type="number"
              disabled={!edit}
              value={cur.archiveDays}
              onChange={(e) => setF({ ...f, archiveDays: Number(e.target.value) })}
            />
          </Field>
          <Field label="Хранение событий, дней">
            <Input
              type="number"
              disabled={!edit}
              value={cur.eventDays}
              onChange={(e) => setF({ ...f, eventDays: Number(e.target.value) })}
            />
          </Field>
          <Field label="«Нет сигнала» через, минут">
            <Input
              type="number"
              disabled={!edit}
              value={cur.offlineAfterMinutes}
              onChange={(e) => setF({ ...f, offlineAfterMinutes: Number(e.target.value) })}
            />
          </Field>
        </div>
        {edit ? (
          <div className="mt-4">
            <Button variant="primary" loading={save.isPending} disabled={!f} onClick={() => save.mutate()}>
              Сохранить
            </Button>
            <ErrorBox error={save.error} className="mt-3" />
          </div>
        ) : null}
      </section>
      {admin ? (
        <>
          <ConsentsSection policyReady={Boolean(s?.policyApprovedOn)} />
          <KeysSection />
        </>
      ) : null}
    </div>
  );
}

function ConsentsSection({ policyReady }: { policyReady: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['video', 'consents'],
    queryFn: () =>
      api<
        Array<{
          id: string;
          userName: string;
          signedOn: string;
          revokedAt: string | null;
          inForce: boolean;
          templatesDeletedAt: string | null;
        }>
      >('/video/consents'),
  });
  const members = useQuery({
    queryKey: ['admin-members-staff'],
    queryFn: () =>
      api<Array<{ userId: string; fullName: string; kind: string; status: string }>>('/admin/members'),
  });
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [signedOn, setSignedOn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const add = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append('file', file!);
      const uploaded = await api<{ id: string }>('/files', { method: 'POST', form });
      return api('/video/consents', {
        method: 'POST',
        body: { userId, signedOn, documentFileId: uploaded.id },
      });
    },
    onSuccess: () => {
      toast('Согласие внесено');
      setOpen(false);
      setFile(null);
      qc.invalidateQueries({ queryKey: ['video'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api(`/video/consents/${id}/revoke`, { method: 'POST', body: { reason: 'Отзыв согласия' } }),
    onSuccess: () => {
      toast('Согласие отозвано: опознание остановлено, шаблоны лица подлежат удалению');
      qc.invalidateQueries({ queryKey: ['video'] });
    },
  });
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-[16px] font-semibold">Согласия на биометрию</h3>
        <Button size="sm" disabled={!policyReady} onClick={() => setOpen(true)}>
          Внести согласие
        </Button>
      </div>
      <p className="mb-3 text-[13px] text-ink-2">
        Лицо опознаётся только у сотрудника с письменным согласием (скан в портале), только на КПП с
        табличкой. Отзыв или увольнение останавливают опознание.
      </p>
      {!policyReady ? (
        <p className="mb-3 text-[13px] text-warning">Сначала утвердите положение выше.</p>
      ) : null}
      {q.data?.length ? (
        <ul className="divide-y divide-line">
          {q.data.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[14px]">
              <span>
                {c.userName} · от {c.signedOn}{' '}
                {c.inForce ? (
                  <Badge tone="success">Действует</Badge>
                ) : (
                  <Badge tone="neutral">
                    {c.revokedAt ? 'Отозвано' : 'Не действует: сотрудник отключён'}
                    {c.templatesDeletedAt ? ', шаблоны удалены' : ', шаблоны ждут удаления'}
                  </Badge>
                )}
              </span>
              {c.inForce ? (
                <Button size="sm" loading={revoke.isPending} onClick={() => revoke.mutate(c.id)}>
                  Отозвать
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Согласий нет: подсчёт людей идёт без опознания.</p>
      )}
      <ErrorBox error={revoke.error} className="mt-3" />
      {open ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setOpen(false)}
          title="Письменное согласие"
          width="sm"
          footer={
            <>
              <Button onClick={() => setOpen(false)}>Отмена</Button>
              <Button
                variant="primary"
                loading={add.isPending}
                disabled={!userId || !signedOn || !file}
                onClick={() => add.mutate()}
              >
                Внести
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <Field label="Сотрудник">
              <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Выберите</option>
                {(members.data ?? [])
                  .filter((m) => m.kind === 'staff' && m.status === 'active')
                  .map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.fullName}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Дата подписи">
              <Input type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
            </Field>
            <Field label="Скан согласия" hint="PDF или фото подписанного документа">
              <Input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Field>
            <ErrorBox error={add.error} />
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

function KeysSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['video', 'keys'],
    queryFn: () =>
      api<Array<{ id: string; name: string; lastUsedAt: string | null; revokedAt: string | null }>>(
        '/video/ingest-keys',
      ),
  });
  const [name, setName] = useState('');
  const [shown, setShown] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ key: string }>('/video/ingest-keys', { method: 'POST', body: { name } }),
    onSuccess: (r) => {
      setShown(r.key);
      setName('');
      qc.invalidateQueries({ queryKey: ['video', 'keys'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/video/ingest-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['video', 'keys'] }),
  });
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <h3 className="mb-1 text-[16px] font-semibold">Ключи видеосервера</h3>
      <p className="mb-3 text-[13px] text-ink-2">
        По ключу видеосервер площадки передаёт состояние камер и события. Ключ показывается один раз.
      </p>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Field label="Название сервера">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button loading={create.isPending} disabled={name.trim().length < 2} onClick={() => create.mutate()}>
          Выдать ключ
        </Button>
      </div>
      <ErrorBox error={create.error} className="mb-3" />
      {shown ? (
        <div className="mb-3 rounded-md bg-tile p-3 text-[13px]">
          <p className="mb-1 font-semibold">Скопируйте ключ сейчас — повторно он не показывается:</p>
          <code className="break-all">{shown}</code>
        </div>
      ) : null}
      <ul className="divide-y divide-line">
        {(q.data ?? []).map((k) => (
          <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[14px]">
            <span>
              {k.name}{' '}
              {k.revokedAt ? (
                <Badge tone="neutral">Отозван</Badge>
              ) : (
                <span className="text-[12px] text-ink-2">
                  {k.lastUsedAt ? `использован ${dateTime(k.lastUsedAt)}` : 'ещё не использовался'}
                </span>
              )}
            </span>
            {!k.revokedAt ? (
              <Button size="sm" loading={revoke.isPending} onClick={() => revoke.mutate(k.id)}>
                Отозвать
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
